import { Box, Button, Chip, Divider, List, ListItem, ListItemText, Paper, Skeleton, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';

import AdminPageHeader from './AdminPageHeader';
import { useAuth } from '../../contexts/AuthContext';
import { useAPIClients, useUsers } from '../../api/admin';
import { useSSOSetting } from '../../api/sso';
import { useGisServicesSetting, usePublishedLayers } from '../../api/gis';
import { useAIAssistantsSetting, useAdminMCPConnections } from '../../api/mcp';
import { useRealtimeKindsSetting } from '../../api/realtimeKinds';
import { useAgentIncidentsSetting } from '../../api/agentSessions';
import { useChildTenantsSetting } from '../../api/tenants';
import { useActivity, type ActivityEvent } from '../../api/activity';
import type { AdminPageKey } from './adminNavigation';

/**
 * The admin landing page: where things stand, the tasks administrators do
 * most, and what changed recently. Every card deep-links to the page that
 * owns the setting, so nobody has to remember where it lives.
 */

interface Props {
  isFisci: boolean;
}

const pathFor = (page: AdminPageKey) => (page === 'overview' ? '/admin' : `/admin/${page}`);

function StatCard({
  label,
  value,
  detail,
  page,
  loading,
}: {
  label: string;
  value: string;
  detail: string;
  page: AdminPageKey;
  loading?: boolean;
}) {
  const navigate = useNavigate();
  return (
    <Paper
      variant="outlined"
      onClick={() => navigate(pathFor(page))}
      sx={{ p: 2, borderRadius: 3, cursor: 'pointer', '&:hover': { borderColor: 'secondary.main' } }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '.05em' }}>
        {label}
      </Typography>
      {loading ? (
        <Skeleton width="60%" height={32} />
      ) : (
        <Typography variant="h6" sx={{ fontWeight: 500, lineHeight: 1.3, mt: 0.5 }}>
          {value}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        {detail}
      </Typography>
    </Paper>
  );
}

const TASKS: { label: string; page: AdminPageKey; fisci?: boolean }[] = [
  { label: 'Add a person', page: 'people' },
  { label: 'Turn on or require single sign-on', page: 'sign-in' },
  { label: 'Create an API key', page: 'api-keys' },
  { label: 'Connect ArcGIS or QGIS', page: 'gis' },
  { label: 'Tell the incident agent what you expect', page: 'standing-orders' },
  { label: 'Change the logo or organisation name', page: 'organisation' },
  { label: "Change what this organisation's plan includes", page: 'entitlements', fisci: true },
];

/** One plain sentence per audit event, in the administrator's words. */
function describe(event: ActivityEvent): string {
  const who = event.actor?.username || 'Someone';
  const target = (event.extra.target_username as string | undefined) || (event.extra.username as string | undefined);
  switch (event.event) {
    case 'login_success':
      return `${who} signed in`;
    case 'sso_login_success':
      return `${who} signed in with single sign-on`;
    case 'login_failure':
      return `Failed sign-in attempt${target ? ` for ${target}` : ''}`;
    case 'sso_login_failure':
      return `Single sign-on failed${event.reason ? ` (${event.reason.replace(/_/g, ' ')})` : ''}`;
    case 'sso_jit_created':
      return `${who} signed in for the first time and an account was created`;
    case 'logout':
      return `${who} signed out`;
    case 'user_deactivated':
      return `${who} deactivated ${target ?? 'a person'}`;
    case 'user_reactivated':
      return `${who} reactivated ${target ?? 'a person'}`;
    case 'password_reset_by_admin':
      return `${who} reset ${target ? `${target}'s` : "someone's"} password`;
    case 'password_changed':
      return `${who} changed their password`;
    case 'sso_config_changed':
      return `${who} changed the sign-in settings`;
    case 'sso_config_deleted':
      return `${who} removed single sign-on`;
    case 'tenant_created':
      return `${who} created a client organisation`;
    case 'tenant_renamed':
      return `${who} changed the organisation's ${((event.extra.fields as string[] | undefined) || ['details']).join(' and ')}`;
    case 'tenant_access_granted':
      return `${who} gave a colleague access to a client organisation`;
    case 'tenant_access_revoked':
      return `${who} removed a colleague's access to a client organisation`;
    case 'tenant_switch':
      return `${who} entered a client organisation`;
    case 'tenant_switch_home':
      return `${who} returned to this organisation`;
    default:
      return `${who}: ${event.event.replace(/_/g, ' ')}`;
  }
}

function when(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export default function AdminOverview({ isFisci }: Props) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const users = useUsers();
  const sso = useSSOSetting();
  const apiKeys = useAPIClients();
  const gis = useGisServicesSetting();
  const published = usePublishedLayers();
  const ai = useAIAssistantsSetting();
  const connections = useAdminMCPConnections();
  const realtime = useRealtimeKindsSetting();
  const agent = useAgentIncidentsSetting();
  const children = useChildTenantsSetting();
  const activity = useActivity(12);

  const people = users.data ?? [];
  const active = people.filter((u) => u.is_active !== false);
  const admins = active.filter((u) => u.is_staff);
  const deactivated = people.length - active.length;

  const provider = sso.data?.provider;
  const ssoValue = !provider || !provider.enabled
    ? 'Password only'
    : provider.provider_kind === 'google_shared'
      ? 'Google Workspace'
      : provider.display_name || 'Your identity provider';
  const ssoDetail = provider && provider.enabled
    ? `${(provider.allowed_email_domains ?? []).join(', ')}${provider.enforce_sso ? ' · required' : ' · passwords still work'}`
    : 'Everyone signs in with a password';

  const keys = apiKeys.data ?? [];
  const activeKeys = keys.filter((k) => k.is_active).length;

  const fires = (published.data ?? []).length;
  const retention = gis.data?.gis_retention_days;

  const conns = (connections.data ?? []).filter((c) => c.is_active !== false).length;

  const kinds = realtime.data?.realtime_kinds;
  const included: string[] = [];
  if (kinds?.hotspot) included.push('Satellite hotspots');
  if (kinds?.lightning) included.push('Lightning');
  if (kinds?.hazard) included.push('Hazard warnings');
  if (agent.data?.allow_agent_incidents) included.push('AI incident monitoring');
  if (children.data?.allow_child_tenants) included.push('Client organisations');

  return (
    <Box>
      <AdminPageHeader
        title="Overview"
        description={`${user?.tenant_name ?? 'Your organisation'} · everything about who can use Mitigate here and how it connects to your other systems.`}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)', xl: 'repeat(6, 1fr)' }, gap: 2 }}>
        <StatCard
          label="People"
          value={`${active.length}`}
          detail={`${admins.length} administrator${admins.length === 1 ? '' : 's'}${deactivated ? ` · ${deactivated} deactivated` : ''}`}
          page="people"
          loading={users.isLoading}
        />
        <StatCard label="Sign-in" value={ssoValue} detail={ssoDetail} page="sign-in" loading={sso.isLoading} />
        <StatCard
          label="API keys"
          value={`${activeKeys} active`}
          detail={keys.length > activeKeys ? `${keys.length - activeKeys} disabled` : keys.length ? 'All keys enabled' : 'No keys yet'}
          page="api-keys"
          loading={apiKeys.isLoading}
        />
        <StatCard
          label="GIS services"
          value={fires ? `${fires} fire${fires === 1 ? '' : 's'} published` : 'Nothing published'}
          detail={
            gis.data?.environment_configured === false
              ? 'Not available in this environment'
              : retention ? `Kept for ${retention} days` : 'Kept until removed'
          }
          page="gis"
          loading={gis.isLoading || published.isLoading}
        />
        <StatCard
          label="AI assistants"
          value={`${conns} connection${conns === 1 ? '' : 's'}`}
          detail={ai.data?.allow_ai_assistants === false ? 'Connecting is switched off' : 'People may connect assistants'}
          page="ai-assistants"
          loading={connections.isLoading}
        />
        <StatCard
          label="Your plan includes"
          value={included.length ? included.slice(0, 2).join(' · ') : 'Core features'}
          detail={
            included.length > 2
              ? `and ${included.slice(2).join(', ')}`
              : isFisci ? 'FiSci staff can change this under Entitlements' : 'Contact FiSci to add live feeds or AI monitoring'
          }
          page={isFisci ? 'entitlements' : 'standing-orders'}
          loading={realtime.isLoading}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>Common tasks</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            The things administrators do most, in one click.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {TASKS.filter((t) => !t.fisci || isFisci).map((task) => (
              <Button
                key={task.label}
                variant="outlined"
                color="inherit"
                endIcon={<ArrowForwardIcon fontSize="small" sx={{ opacity: 0.5 }} />}
                onClick={() => navigate(pathFor(task.page))}
                sx={{ justifyContent: 'space-between', textTransform: 'none', borderColor: 'divider', px: 1.5 }}
              >
                {task.label}
              </Button>
            ))}
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>Recent activity</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Sign-ins, account changes and setting changes in your organisation.
          </Typography>
          {activity.isLoading ? (
            <Skeleton height={120} />
          ) : !activity.data?.length ? (
            <Typography variant="body2" color="text.secondary">Nothing recorded yet.</Typography>
          ) : (
            <List dense disablePadding>
              {activity.data.map((event, i) => (
                <Box key={event.id}>
                  {i > 0 && <Divider component="li" />}
                  <ListItem disableGutters sx={{ py: 0.75 }}>
                    <Chip
                      label={when(event.at)}
                      size="small"
                      variant="outlined"
                      sx={{ mr: 1.5, minWidth: 76, fontSize: 11 }}
                    />
                    <ListItemText primary={describe(event)} slotProps={{ primary: { variant: 'body2' } }} />
                  </ListItem>
                </Box>
              ))}
            </List>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
